import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Dialog, Stack, Text, Strong, Actions, Button, ButtonLink, IconNewWindow, TextLinkButton, Box } from "braid-design-system";
import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "@vocab/react";
import { translations } from "../../../components/ProfileCardDescription/.vocab/index.mjs";
import { text } from "./ProfileCardDescription.css.mjs";
const createOpenDialogFn = (dialogStateFn) => () => {
  dialogStateFn(true);
  const historyState = history.state;
  const newState = { ...historyState, dialogOpen: true };
  history.pushState(newState, "Dialog open");
};
const createCloseDialogFn = (dialogStateFn) => () => {
  dialogStateFn(false);
  history.go(-1);
};
const useDialogHistoryManager = (dialogStateFn) => {
  useEffect(() => {
    const windowHistoryListener = () => {
      const historyState = history.state;
      const newState = { ...historyState, dialogOpen: false };
      history.replaceState(newState, "Dialog closed");
      dialogStateFn(false);
    };
    window.addEventListener("popstate", windowHistoryListener);
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
    return () => {
      window.removeEventListener("popstate", windowHistoryListener);
    };
  }, [dialogStateFn]);
  const openDialog = useMemo(
    () => createOpenDialogFn(dialogStateFn),
    [dialogStateFn]
  );
  const closeDialog = useMemo(
    () => createCloseDialogFn(dialogStateFn),
    [dialogStateFn]
  );
  return {
    openDialog,
    closeDialog
  };
};
const ExternalUrlDialog = ({
  onClose,
  externalUrl,
  open,
  brand
}) => {
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsx(
    Dialog,
    {
      id: "externalUrlDialog",
      width: "small",
      onClose,
      closeLabel: t("Close"),
      open,
      title: t("Are you sure you want to leave brand?", { brand }),
      children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
        /* @__PURE__ */ jsx(Text, { children: t("The link will take you to a site outside...", {
          externalUrl,
          brand,
          Strong: (v) => /* @__PURE__ */ jsx(Strong, { children: v }, "confirmLeavingMessage"),
          br: () => /* @__PURE__ */ jsx("br", {}, "confirmLeavingMessageLineBreak")
        }) }),
        /* @__PURE__ */ jsxs(Actions, { children: [
          /* @__PURE__ */ jsx(Button, { onClick: onClose, tone: "formAccent", children: t("Return") }),
          /* @__PURE__ */ jsxs(
            ButtonLink,
            {
              href: externalUrl,
              onClick: onClose,
              target: "_blank",
              rel: "noopener",
              role: "button",
              tone: "formAccent",
              variant: "ghost",
              children: [
                t("Go to site"),
                " ",
                /* @__PURE__ */ jsx(
                  IconNewWindow,
                  {
                    alignY: "lowercase",
                    title: t("Opens in a new tab"),
                    titleId: "externalLinkIcon"
                  }
                )
              ]
            }
          )
        ] })
      ] })
    }
  );
};
var createSites = (input) => input;
var siteConfig = createSites({
  "candidate-seek-root": {
    host: "www.seek.com",
    stagingHost: "www.staging.seek.com",
    languages: ["en"],
    classification: {
      product: "candidate",
      brand: "seek",
      country: null
    }
  },
  "candidate-seek-au": {
    host: "www.seek.com.au",
    stagingHost: "www.staging.seek.com.au",
    alternatives: {
      topLevelRegion: {
        host: "au.seek.com",
        stagingHost: "au.staging.seek.com"
      }
    },
    languages: ["en"],
    classification: {
      product: "candidate",
      brand: "seek",
      country: "au"
    }
  },
  "candidate-seek-nz": {
    host: "www.seek.co.nz",
    stagingHost: "www.staging.seek.co.nz",
    alternatives: {
      topLevelRegion: {
        host: "nz.seek.com",
        stagingHost: "nz.staging.seek.com"
      }
    },
    languages: ["en"],
    classification: {
      product: "candidate",
      brand: "seek",
      country: "nz"
    }
  },
  "candidate-jobsdb-hk": {
    host: "hk.jobsdb.com",
    stagingHost: "hk.staging.jobsdb.com",
    languages: ["en", "zh"],
    classification: {
      product: "candidate",
      brand: "jobsdb",
      country: "hk"
    }
  },
  "candidate-jobsdb-th": {
    host: "th.jobsdb.com",
    stagingHost: "th.staging.jobsdb.com",
    languages: ["en", "th"],
    classification: {
      product: "candidate",
      brand: "jobsdb",
      country: "th"
    }
  },
  "candidate-jobsdb-root": {
    host: "www.jobsdb.com",
    stagingHost: "www.staging.jobsdb.com",
    languages: ["en"],
    classification: {
      product: "candidate",
      brand: "jobsdb",
      country: null
    }
  },
  "candidate-jobstreet-sg": {
    host: "sg.jobstreet.com",
    stagingHost: "sg.staging.jobstreet.com",
    languages: ["en"],
    classification: {
      product: "candidate",
      brand: "jobstreet",
      country: "sg"
    }
  },
  "candidate-jobstreet-id": {
    host: "id.jobstreet.com",
    stagingHost: "id.staging.jobstreet.com",
    languages: ["id", "en"],
    classification: {
      product: "candidate",
      brand: "jobstreet",
      country: "id"
    }
  },
  "candidate-jobstreet-ph": {
    host: "ph.jobstreet.com",
    stagingHost: "ph.staging.jobstreet.com",
    languages: ["en"],
    classification: {
      product: "candidate",
      brand: "jobstreet",
      country: "ph"
    }
  },
  "candidate-jobstreet-my": {
    host: "my.jobstreet.com",
    stagingHost: "my.staging.jobstreet.com",
    languages: ["en"],
    classification: {
      product: "candidate",
      brand: "jobstreet",
      country: "my"
    }
  },
  "candidate-jobstreet-root": {
    host: "www.jobstreet.com",
    stagingHost: "www.staging.jobstreet.com",
    languages: ["en"],
    classification: {
      product: "candidate",
      brand: "jobstreet",
      country: null
    }
  },
  "employer-seek-au": {
    host: "talent.seek.com.au",
    stagingHost: "talent.staging.seek.com.au",
    alternatives: {
      topLevelRegion: {
        host: "au.employer.seek.com",
        stagingHost: "au.employer.staging.seek.com"
      }
    },
    languages: ["en"],
    classification: {
      product: "employer",
      brand: "seek",
      country: "au"
    }
  },
  "employer-seek-nz": {
    host: "talent.seek.co.nz",
    stagingHost: "talent.staging.seek.co.nz",
    alternatives: {
      topLevelRegion: {
        host: "nz.employer.seek.com",
        stagingHost: "nz.employer.staging.seek.com"
      }
    },
    languages: ["en"],
    classification: {
      product: "employer",
      brand: "seek",
      country: "nz"
    }
  },
  "employer-seek-hk": {
    host: "hk.employer.seek.com",
    stagingHost: "hk.employer.staging.seek.com",
    languages: ["en"],
    classification: {
      product: "employer",
      brand: "seek",
      country: "hk"
    }
  },
  "employer-seek-th": {
    host: "th.employer.seek.com",
    stagingHost: "th.employer.staging.seek.com",
    languages: ["en", "th"],
    classification: {
      product: "employer",
      brand: "seek",
      country: "th"
    }
  },
  "employer-seek-sg": {
    host: "sg.employer.seek.com",
    stagingHost: "sg.employer.staging.seek.com",
    languages: ["en"],
    classification: {
      product: "employer",
      brand: "seek",
      country: "sg"
    }
  },
  "employer-seek-id": {
    host: "id.employer.seek.com",
    stagingHost: "id.employer.staging.seek.com",
    languages: ["id", "en"],
    classification: {
      product: "employer",
      brand: "seek",
      country: "id"
    }
  },
  "employer-seek-ph": {
    host: "ph.employer.seek.com",
    stagingHost: "ph.employer.staging.seek.com",
    languages: ["en"],
    classification: {
      product: "employer",
      brand: "seek",
      country: "ph"
    }
  },
  "employer-seek-my": {
    host: "my.employer.seek.com",
    stagingHost: "my.employer.staging.seek.com",
    languages: ["en"],
    classification: {
      product: "employer",
      brand: "seek",
      country: "my"
    }
  }
});
Object.values(siteConfig).map(
  (site) => site.classification.product
);
Object.values(siteConfig).map(
  (site) => site.classification.brand
);
Object.values(siteConfig).map((site) => site.classification.country).filter(
  (country) => country !== null
);
var objectKeys = (input) => Object.keys(input);
var getAlternatives = (site) => {
  const config = siteConfig[site];
  return "alternatives" in config ? config.alternatives ?? {} : {};
};
var getAlternativeHosts = (site, staging) => Object.values(getAlternatives(site)).map(
  ({ host, stagingHost }) => staging ? stagingHost : host
);
var getAllHostNames = ({
  site,
  staging
}) => {
  const { host, stagingHost } = siteConfig[site];
  const primaryHost = staging ? stagingHost : host;
  const alternativeHosts = getAlternativeHosts(site, staging);
  return [primaryHost, ...alternativeHosts];
};
Object.fromEntries(
  objectKeys(siteConfig).flatMap(
    (site) => [
      ...getAllHostNames({ site, staging: false }),
      ...getAllHostNames({ site, staging: true })
    ].map((hostname) => [hostname, site])
  )
);
const internalUrls = Object.values(siteConfig).map(
  (config) => config.host.replace("www.", "")
);
const urlRegex = /((?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+~#?&//=]*))/g;
const isInternalUrl = (url) => {
  try {
    const targetUrl = new URL(url.startsWith("https") ? url : `https://${url}`);
    return internalUrls.some(
      (internalUrl) => targetUrl.hostname === internalUrl || targetUrl.hostname.endsWith(`.${internalUrl}`)
    );
  } catch {
    return false;
  }
};
const sanitiseUrl = (url) => {
  const matches = url.match(/^https?:\/\//g);
  if (matches === null) {
    return `https://${url}`;
  }
  if (matches[0] === "http://") {
    return url.replace("http", "https");
  }
  return url;
};
const replaceTextWithLinks = (text2, onClickFn, linksAreKeyboardAccessible) => {
  const splitText = text2.split(urlRegex);
  const replacedText = splitText.map((subString, index) => {
    if (index % 2 !== 0) {
      const url = sanitiseUrl(subString);
      return /* @__PURE__ */ jsx(
        TextLinkButton,
        {
          onClick: () => onClickFn(url),
          weight: "weak",
          tabIndex: linksAreKeyboardAccessible ? 0 : -1,
          children: subString
        },
        index
      );
    }
    return subString;
  });
  return replacedText;
};
const ProfileCardDescription = ({
  text: text$1,
  data,
  linksAreKeyboardAccessible = true,
  brand = "SEEK",
  replaceUrlTextWithLinks = true
}) => {
  const [displayDialog, setDisplayDialog] = useState(false);
  const { openDialog, closeDialog } = useDialogHistoryManager(setDisplayDialog);
  const [currentExternalUrl, setCurrentExternalUrl] = useState("");
  const onClickFn = (url) => {
    if (isInternalUrl(url)) {
      window.open(url, "_blank", "noopener");
    } else {
      setCurrentExternalUrl(url);
      openDialog();
    }
  };
  const onClose = () => {
    closeDialog();
  };
  const replacedText = replaceUrlTextWithLinks ? replaceTextWithLinks(text$1, onClickFn, linksAreKeyboardAccessible) : text$1;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Box,
      {
        className: text,
        component: "span",
        data: data ? { ...data } : { "hj-masked": "true" },
        children: replacedText
      }
    ),
    /* @__PURE__ */ jsx(
      ExternalUrlDialog,
      {
        externalUrl: currentExternalUrl,
        open: displayDialog,
        onClose,
        brand
      }
    )
  ] });
};
export {
  ProfileCardDescription,
  useDialogHistoryManager
};
